import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { CheckSquare, X } from 'lucide-react'
import { supabase } from '../lib/supabase/browser'
import { useAuth } from '../contexts/AuthContextCore'
import { useHotel } from '../contexts/HotelContextCore'
import { logger } from '../lib/logger'

// Re-show the reminder this long after a dismissal if the checklist is still incomplete
const SNOOZE_MS = 2 * 60 * 60 * 1000 // 2 hours
// How often we re-check checklist completion while the app is open
const CHECK_INTERVAL_MS = 5 * 60 * 1000 // 5 minutes

type Shift = 'AM' | 'PM' | 'Overnight'

interface ReminderState {
  checklistId: string | null
  checklistName: string
  shift: Shift
  doneCount: number
  totalCount: number
}

function getEasternDateString(date: Date = new Date()) {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/New_York',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).formatToParts(date)
  const y = parts.find(p => p.type === 'year')?.value
  const m = parts.find(p => p.type === 'month')?.value
  const d = parts.find(p => p.type === 'day')?.value
  return `${y}-${m}-${d}`
}

function getEasternHour(date: Date = new Date()) {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    hour: 'numeric',
    hour12: false
  }).formatToParts(date)
  return parseInt(parts.find(p => p.type === 'hour')?.value || '0', 10)
}

// Shift windows in Eastern time, aligned with the built-in checklists:
// MORNING SHIFT 7am-3pm, EVENING SHIFT 3pm-11pm, NIGHT AUDIT SHIFT 11pm-7am
function getCurrentShift(date: Date = new Date()): Shift {
  const hour = getEasternHour(date)
  if (hour >= 7 && hour < 15) return 'AM'
  if (hour >= 15 && hour < 23) return 'PM'
  return 'Overnight'
}

function snoozeKey(hotelId: string, dateStr: string, shift: Shift) {
  return `fd_checklist_reminder_${hotelId}_${dateStr}_${shift}`
}

function isSnoozed(hotelId: string, dateStr: string, shift: Shift) {
  try {
    const raw = localStorage.getItem(snoozeKey(hotelId, dateStr, shift))
    if (!raw) return false
    return Date.now() - parseInt(raw, 10) < SNOOZE_MS
  } catch {
    return false
  }
}

function setSnooze(hotelId: string, dateStr: string, shift: Shift) {
  try {
    localStorage.setItem(snoozeKey(hotelId, dateStr, shift), String(Date.now()))
  } catch {
    // localStorage unavailable - reminder will just reappear, which is acceptable
  }
}

export function DailyChecklistReminder() {
  const { user } = useAuth()
  const { selectedHotel } = useHotel()
  const navigate = useNavigate()
  const [reminder, setReminder] = useState<ReminderState | null>(null)

  const userRole = user?.memberships?.[0]?.role || 'user'

  const checkChecklist = useCallback(async () => {
    if (!user || !selectedHotel || userRole !== 'frontdesk') {
      setReminder(null)
      return
    }

    const now = new Date()
    const today = getEasternDateString(now)
    const shift = getCurrentShift(now)

    if (isSnoozed(selectedHotel.id, today, shift)) {
      setReminder(null)
      return
    }

    try {
      const { data: checklists, error } = await (supabase
        .from('fd_simple_checklists' as any)
        .select('id, checklist_name')
        .eq('hotel_id', selectedHotel.id)
        .eq('assigned_date', today)
        .eq('assigned_shift', shift)
        .limit(1) as unknown as Promise<{ data: any[] | null, error: any }>)

      if (error) {
        logger.error('[ChecklistReminder] Error loading checklist', error)
        setReminder(null)
        return
      }

      const checklist = checklists?.[0]
      if (!checklist) {
        // Built-in checklist for this shift hasn't been created/opened yet today
        setReminder({
          checklistId: null,
          checklistName: shift === 'AM' ? 'MORNING SHIFT' : shift === 'PM' ? 'EVENING SHIFT' : 'NIGHT AUDIT SHIFT',
          shift,
          doneCount: 0,
          totalCount: 0
        })
        return
      }

      const { data: tasks, error: tasksError } = await (supabase
        .from('fd_simple_checklist_tasks' as any)
        .select('id, status')
        .eq('checklist_id', checklist.id) as unknown as Promise<{ data: any[] | null, error: any }>)

      if (tasksError) {
        logger.error('[ChecklistReminder] Error loading checklist tasks', tasksError)
        setReminder(null)
        return
      }

      const allTasks = tasks || []
      const doneCount = allTasks.filter(t => t.status === 'completed').length
      const pendingCount = allTasks.length - doneCount

      if (pendingCount > 0) {
        setReminder({
          checklistId: checklist.id,
          checklistName: checklist.checklist_name,
          shift,
          doneCount,
          totalCount: allTasks.length
        })
      } else {
        setReminder(null)
      }
    } catch (err) {
      logger.error('[ChecklistReminder] Unexpected error', err)
      setReminder(null)
    }
  }, [user, selectedHotel, userRole])

  useEffect(() => {
    checkChecklist()
    const interval = setInterval(checkChecklist, CHECK_INTERVAL_MS)
    return () => clearInterval(interval)
  }, [checkChecklist])

  const dismiss = () => {
    if (selectedHotel && reminder) {
      setSnooze(selectedHotel.id, getEasternDateString(), reminder.shift)
    }
    setReminder(null)
  }

  const openChecklist = () => {
    if (selectedHotel && reminder) {
      setSnooze(selectedHotel.id, getEasternDateString(), reminder.shift)
    }
    const params = new URLSearchParams({ openChecklist: '1' })
    if (reminder?.checklistId) {
      params.set('checklistId', reminder.checklistId)
    }
    setReminder(null)
    navigate(`/front-desk?${params.toString()}`)
  }

  if (!reminder) return null

  const pct = reminder.totalCount > 0 ? Math.round((reminder.doneCount / reminder.totalCount) * 100) : 0

  return (
    <div className="fixed bottom-6 right-6 z-50 w-80 max-w-[calc(100vw-3rem)] animate-[slideUp_0.3s_ease-out]">
      <style>{`@keyframes slideUp { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }`}</style>
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl border border-violet-200 dark:border-violet-800 overflow-hidden">
        <div className="h-1 bg-gradient-to-r from-violet-500 to-purple-500" />
        <div className="p-4">
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-center gap-2">
              <div className="p-2 bg-violet-100 dark:bg-violet-900/30 rounded-lg">
                <CheckSquare className="w-5 h-5 text-violet-600 dark:text-violet-400" />
              </div>
              <div>
                <h4 className="text-sm font-semibold text-gray-900 dark:text-white">Daily Checklist Reminder</h4>
                <p className="text-xs text-gray-500 dark:text-gray-400">{reminder.checklistName}</p>
              </div>
            </div>
            <button
              onClick={dismiss}
              aria-label="Dismiss reminder"
              className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <p className="mt-3 text-sm text-gray-600 dark:text-gray-300">
            {reminder.totalCount > 0
              ? `${reminder.doneCount} of ${reminder.totalCount} tasks completed. Don't forget to finish your shift checklist!`
              : "Your shift checklist hasn't been started yet. Don't forget to complete it!"}
          </p>

          {reminder.totalCount > 0 && (
            <div className="mt-2">
              <div className="w-full bg-gray-200 dark:bg-gray-700 h-2 rounded-full overflow-hidden">
                <div
                  className="h-2 bg-gradient-to-r from-emerald-500 to-blue-500 transition-all duration-500"
                  style={{ width: `${pct}%` }}
                />
              </div>
              <div className="text-xs mt-1 text-gray-600 dark:text-gray-400">{pct}%</div>
            </div>
          )}

          <div className="mt-3 flex gap-2">
            <button
              onClick={openChecklist}
              className="flex-1 px-3 py-2 text-sm font-medium text-white bg-violet-600 hover:bg-violet-700 rounded-lg transition-colors"
            >
              Open Checklist
            </button>
            <button
              onClick={dismiss}
              className="px-3 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              Later
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
